// ─────────────────────────────────────────────────────────────────────────────
// STORED VALUES vs DISPLAY LABELS — read before editing.
//
// Everything in DISEASE_OPTIONS / DIAGNOSIS_OPTIONS is the value that gets
// STORED (Firestore `diagnosis` arrays, the legacy Sheet DIAGNOSIS column,
// study `DISEASE`, "Future Studies - X" statuses, cached eligibility rules).
//
// "IPF" is shown to users as "ILD" but is STILL STORED AS "IPF" — for existing
// records and for every new selection. Never store "ILD", never add it as an
// option, and never migrate existing "IPF" values: every filter, box and match
// compares the stored string, so one stored value keeps old and new patients
// together. Rename things on screen via DIAGNOSIS_DISPLAY + diagnosisLabel /
// statusLabel only. Enforced by diseaseOptions.test.js; see CLAUDE.md.
// ─────────────────────────────────────────────────────────────────────────────

// Study `DISEASE` options (AddStudyModal / EditStudyModal / Dashboard filter).
export const DISEASE_OPTIONS = [
  "Asthma",
  "COPD",
  "Bronchiectasis",
  "Sleep Apnea",
  "Diagnostics",
  "IPF",
  "Obesity",
  "Diabetes",
  "Sarcoid",
  "Heart Disease",
  "Cancer",
  "Alpha-1",
];

// Patient `diagnosis` options — also drives the "Future Studies - X" statuses.
// Order is the order staff already see on the patient page; new ones at the end.
export const DIAGNOSIS_OPTIONS = [
  "Asthma",
  "COPD",
  "Diagnostics",
  "Bronchiectasis",
  "IPF",
  "Sleep Apnea",
  "Obesity",
  "Diabetes",
  "Sarcoid",
  "Heart Disease",
  "Cancer",
  "Alpha-1",
];

// Stored value → on-screen label. Display only; never write these labels back.
export const DIAGNOSIS_DISPLAY = {
  IPF: "ILD",
};

const DISPLAY_LOWER = Object.fromEntries(
  Object.entries(DIAGNOSIS_DISPLAY).map(([k, v]) => [k.toLowerCase(), v])
);

// Label for a stored diagnosis / disease value. Unknown values pass through.
export const diagnosisLabel = (value) => {
  if (value === null || value === undefined) return value;
  const s = String(value);
  return DISPLAY_LOWER[s.trim().toLowerCase()] || s;
};

// Label for a stored status ("Future Studies - IPF" → "Future Studies - ILD").
export const statusLabel = (status) => {
  if (!status) return status;
  const s = String(status);
  const m = s.match(/^(Future Studies - )(.+)$/i);
  if (!m) return s;
  return `${m[1]}${diagnosisLabel(m[2])}`;
};

// Patient text-message survey (ReferralQuestionnairePage) stores its own
// condition names in referral_submissions `conditions[].name`. Same rule: the
// stored name never changes; this only relabels it on screen.
export const SURVEY_CONDITION_DISPLAY = {
  "Pulmonary Fibrosis": "ILD / Pulmonary Fibrosis",
};

export const surveyConditionLabel = (name) => {
  if (name === null || name === undefined) return name;
  return SURVEY_CONDITION_DISPLAY[String(name)] || String(name);
};

// Normalize any input (ASTHMA → Asthma, copd → COPD, etc.)
export const normalizeDisease = (value) => {
  if (!value) return "";

  const normalized = value.toString().trim().toLowerCase();

  const match = DISEASE_OPTIONS.find(
    (d) => d.toLowerCase() === normalized
  );

  return match || value; // fallback if something unexpected
};
